import React, { useEffect, useRef, useState } from "react";
import { ref, getDownloadURL } from "firebase/storage";
import GeoTIFF, { fromUrl } from "geotiff";
import { useStorage } from "reactfire";
import { AiOutlineLeft, AiOutlineRight } from "react-icons/ai";

interface TiffViewerProps {
  filePath: string; // Path to the TIFF file in Firebase Storage
}

const TiffViewer: React.FC<TiffViewerProps> = ({ filePath }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0); // Track current page
  const [totalPages, setTotalPages] = useState<number>(0); // Track total pages
  const [tiff, setTiff] = useState<GeoTIFF | null>(null);
  const storage = useStorage();
  const renderPage = async (tiff: GeoTIFF, pageIndex: number) => {
    try {
      const image = await tiff.getImage(pageIndex);
      const raster = await image.readRasters({ interleave: true });

      // Set up canvas
      const canvas = canvasRef.current;
      if (!canvas) {
        throw new Error("Canvas element not found");
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error("Failed to get canvas context");
      }

      canvas.width = image.getWidth();
      canvas.height = image.getHeight();

      // Create image data
      const imageData = ctx.createImageData(image.getWidth(), image.getHeight());
      for (let i = 0, j = 0; i < raster.length; i += image.getSamplesPerPixel(), j += 4) {
        imageData.data[j] = raster[i] as number; // Red
        imageData.data[j + 1] = raster[i + 1] as number || raster[i] as number; // Green
        imageData.data[j + 2] = raster[i + 2] as number || raster[i] as number; // Blue
        imageData.data[j + 3] = 255; // Alpha
      }

      ctx.putImageData(imageData, 0, 0);
    } catch (err: any) {
      setError(err.message || "An error occurred while rendering the page");
    }
  };
  useEffect(() => {
    const loadTiff = async () => {
      try {
        if (filePath === '' || !filePath) return;

        console.log('Loading file ' + filePath)

        setLoading(true);
        setError(null);

        // Get the file URL from Firebase Storage
        const url = await getDownloadURL(ref(storage, filePath));

        console.log('Loading file downloadURL: ' + url)

        // Parse and render the TIFF using geotiff.js
        setTiff(await fromUrl(url));
      } catch (err: any) {
        setError(err.message || "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    loadTiff();
  }, [filePath, storage]);

  useEffect(() => {
    const loadPages = async () => {
      if (tiff) {
        const count = await tiff.getImageCount();
        setTotalPages(count);
        await renderPage(tiff, currentPage);
      }
    }
    loadPages();
  }, [tiff, currentPage]);

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
  };

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  return (
    <div className="w-full">
      {loading && <p>Loading TIFF page...</p>}
      {error && <p>Error: {error}</p>}
      <div className="flex row-auto justify-between px-4">
        <button onClick={handlePrevious} disabled={currentPage === 0}>
          <AiOutlineLeft />
        </button>
        <span style={{ margin: "0 10px" }}>
          Page {currentPage + 1} of {totalPages}
        </span>
        <button onClick={handleNext} disabled={currentPage === totalPages - 1}>
          <AiOutlineRight />

        </button>
      </div>
      <canvas ref={canvasRef} className="w-full" />

    </div>
  );
};

export default TiffViewer;